/* Form-family park: the legacy combobox retires with its last parked callers, in the Table and details-tab rounds (component-map.md). */
import { useFormControl } from '@chakra-ui/react';
import { GroupBase, SelectInstance } from 'chakra-react-select';
import { ForwardedRef, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fixedForwardRef } from '../../utils';
import { ComboboxComponents, useComboboxComponents } from './combobox-components';
import { ComboboxVariant, getChakraStyles } from './combobox-styles';
import { tagNewOption } from './new-option';
import { Select, SelectProps } from './select';

type MenuPlacement = 'top' | 'bottom';

export type ComboboxProps<
  TOption,
  IsMulti extends boolean = false,
  TGroup extends GroupBase<TOption> = GroupBase<TOption>,
> = Omit<
  SelectProps<TOption, IsMulti, TGroup>,
  'ariaLiveMessages' | 'classNames' | 'styles' | 'chakraStyles' | 'components' | 'variant'
> & {
  groupSize?: number;
  hideClearIndicator?: boolean;
  variant?: ComboboxVariant;
  singleAsMulti?: boolean;
  components?: ComboboxComponents<TOption>;
};

function Combobox_<
  TOption extends object,
  IsMulti extends boolean = false,
  TGroup extends GroupBase<TOption> = GroupBase<TOption>,
>(
  {
    variant = 'inline',
    menuPlacement = 'auto',
    menuPortalTarget,
    menuPosition,
    hideClearIndicator,
    ...props
  }: ComboboxProps<TOption, IsMulti, TGroup>,
  ref: ForwardedRef<SelectInstance<TOption, IsMulti, TGroup>>,
) {
  const { t } = useTranslation('ui');
  const formControlProps = useFormControl({});
  const isReadOnly = formControlProps.readOnly || props.isReadOnly;
  const isMulti = props.isMulti;
  const onChangeProp = props.onChange;
  const suppressFocusScroll = useRef(false);
  const [resolvedMenuPlacement, setResolvedMenuPlacement] = useState<MenuPlacement>(
    menuPlacement === 'top' ? 'top' : 'bottom',
  );
  const resolvedMenuPortalTarget =
    menuPortalTarget ??
    (variant === 'inline' && typeof document !== 'undefined' ? document.body : undefined);
  const resolvedMenuPosition = menuPosition ?? (resolvedMenuPortalTarget ? 'fixed' : undefined);
  const onMenuPlacementChange = useCallback((placement: MenuPlacement) => {
    setResolvedMenuPlacement((currentPlacement) =>
      currentPlacement === placement ? currentPlacement : placement,
    );
  }, []);
  const onChange = useCallback(
    (...args: Parameters<NonNullable<typeof props.onChange>>) => {
      const action = args[1].action;
      if (
        isMulti &&
        (action === 'select-option' || action === 'create-option' || action === 'deselect-option')
      ) {
        suppressFocusScroll.current = true;
      }
      onChangeProp?.(...args);
    },
    [isMulti, onChangeProp],
  );

  return (
    <Select
      isClearable={false}
      backspaceRemovesValue={false}
      tabSelectsValue={false}
      blurInputOnSelect={!props.isMulti}
      closeMenuOnSelect={!props.isMulti}
      placeholder={t('combobox.placeholder')}
      loadingMessage={() => t('combobox.loading')}
      noOptionsMessage={() => t('combobox.noOptions')}
      menuPlacement={menuPlacement}
      menuPortalTarget={resolvedMenuPortalTarget}
      menuPosition={resolvedMenuPosition}
      {...({
        resolvedMenuPlacement,
        onMenuPlacementChange,
      } as unknown as Partial<SelectProps<TOption, IsMulti, TGroup>>)}
      {...tagNewOption({ ...props, onChange })}
      variant="unstyled"
      chakraStyles={getChakraStyles<TOption, IsMulti, TGroup>(variant)}
      components={useComboboxComponents({ ...props, hideClearIndicator }, suppressFocusScroll)}
      isDisabled={props.isDisabled || isReadOnly}
      {...(isReadOnly && { menuIsOpen: false })}
      // When using a portal, set a high z-index so the menu appears above modals and other overlays
      {...(resolvedMenuPortalTarget && {
        styles: {
          menuPortal: (base: object) => ({ ...base, zIndex: 'var(--chakra-zIndices-modal, 1400)' }),
        },
      })}
      ref={ref}
    />
  );
}

export const Combobox = fixedForwardRef(Combobox_);
